/**
 * Adds the active Shopify locale prefix (e.g. /es, /de) to links that point
 * to Enzuzo-hosted pages, which don't get the prefix automatically.
 *
 * HOW IT WORKS
 * 1. Reads window.Shopify.routes.root — Shopify always keeps this in sync with
 *    the visitor's current market/locale (e.g. "/", "/es/", "/de/", "/en-ca/").
 * 2. Scans the page for <a href> links matching one of ENZUZO_PATHS
 *    (this includes the footer AND the Enzuzo cookie banner).
 * 3. If a matching link isn't already prefixed with the current locale,
 *    rewrites it.
 *
 * WHY THE OBSERVER
 * Enzuzo injects its cookie/consent banner into the DOM a few seconds after
 * the page loads (you identified it as an element inside `.ez-consent`).
 * Those links don't exist yet at DOMContentLoaded, so a MutationObserver
 * watches for `.ez-consent` appearing (or changing) and re-runs the fix at
 * that moment, instead of guessing a fixed delay.
 */
(function () {
  /* ---------------- CONFIG: edit this list ---------------- */
  var ENZUZO_PATHS = [
    '/policies/privacy-policy',
    '/policies/privacy-policy-2'
    // add more Enzuzo page paths here, always without any locale prefix
  ];

  // Class name Enzuzo uses on its injected consent banner wrapper.
  var ENZUZO_BANNER_SELECTOR = '.ez-consent';
  /* ---------------------------------------------------------- */

  function trimSlashes(str) {
    return str.replace(/^\/+|\/+$/g, '');
  }

  // Current locale prefix, e.g. "" for default, "es" for /es/, "en-ca" for /en-ca/
  function getActiveLocalePrefix() {
    var root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return trimSlashes(root); // "" when there's no prefix
  }

  function isEnzuzoPath(pathname) {
    var clean = '/' + trimSlashes(pathname);
    return ENZUZO_PATHS.indexOf(clean) !== -1;
  }

  function pathAlreadyHasLocale(pathname, locale) {
    var segments = trimSlashes(pathname).split('/');
    return segments[0] === locale;
  }

  // Scans the whole document each time — cheap, since it's just a handful
  // of <a> tags filtered against a short path list, and it's idempotent
  // (safe to call repeatedly; already-fixed links are skipped).
  function fixEnzuzoLinks() {
    var locale = getActiveLocalePrefix();
    if (!locale) return; // default market/language — no prefix should be added

    var links = document.querySelectorAll('a[href]');

    links.forEach(function (link) {
      var href = link.getAttribute('href');
      if (!href) return;

      var url;
      try {
        url = new URL(href, window.location.origin);
      } catch (e) {
        return; // malformed href, skip it
      }

      if (url.origin !== window.location.origin) return; // external link, leave alone
      if (!isEnzuzoPath(url.pathname)) return; // not one of our listed pages
      if (pathAlreadyHasLocale(url.pathname, locale)) return; // already correct

      var newPath = '/' + locale + '/' + trimSlashes(url.pathname);
      link.setAttribute('href', newPath + url.search + url.hash);
    });
  }

  function nodeIsOrContainsBanner(node) {
    if (node.nodeType !== 1) return false; // elements only
    if (node.matches && node.matches(ENZUZO_BANNER_SELECTOR)) return true;
    if (node.querySelector && node.querySelector(ENZUZO_BANNER_SELECTOR)) return true;
    return false;
  }

  function mutationTouchesBanner(mutation) {
    var target = mutation.target;
    if (target && target.closest && target.closest(ENZUZO_BANNER_SELECTOR)) {
      return true;
    }
    for (var i = 0; i < mutation.addedNodes.length; i++) {
      if (nodeIsOrContainsBanner(mutation.addedNodes[i])) return true;
    }
    return false;
  }

  var debounceTimer;
  function scheduleFix() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fixEnzuzoLinks, 100);
  }

  function startObserving() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutationTouchesBanner(mutations[i])) {
          scheduleFix();
          return; // one schedule per batch is enough
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Fix whatever's already on the page (e.g. footer links) once the DOM is ready...
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      fixEnzuzoLinks();
      startObserving();
    });
  } else {
    fixEnzuzoLinks();
    startObserving();
  }
})();